import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Button from '../components/common/Button';

const AuthDiagnostic: React.FC = () => {
  const { user, session, loading } = useAuth();
  const [diagnostics, setDiagnostics] = useState<any>({});
  const [sessionCheck, setSessionCheck] = useState<any>(null);

  const runDiagnostics = useCallback(async () => {
    // Check environment
    const env = {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      supabaseUrl: process.env.REACT_APP_SUPABASE_URL,
      hasAnonKey: !!process.env.REACT_APP_SUPABASE_ANON_KEY,
    };

    // Check localStorage
    const storageKeys = Object.keys(localStorage).filter(key => 
      key.includes('supabase') || key.includes('sb-')
    );
    
    const storageData: any = {};
    storageKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        storageData[key] = value ? JSON.parse(value) : null;
      } catch {
        storageData[key] = localStorage.getItem(key);
      }
    });

    // Check current session
    const { data: { session: currentSession }, error } = await supabase.auth.getSession();
    setSessionCheck({ session: currentSession, error });

    // Check cookies
    const cookies = document.cookie.split(';').map(c => c.trim());
    const supabaseCookies = cookies.filter(c => 
      c.includes('supabase') || c.includes('sb-')
    );

    setDiagnostics({
      environment: env,
      authContext: {
        user: user ? { id: user.id, email: user.email } : null,
        session: session ? { expires_at: session.expires_at } : null,
        loading,
      },
      localStorage: {
        keys: storageKeys,
        data: storageData,
      },
      directSessionCheck: sessionCheck,
      cookies: supabaseCookies,
      timestamp: new Date().toISOString(),
    });
  }, [user, session, loading, sessionCheck]);

  useEffect(() => {
    runDiagnostics();
  }, [runDiagnostics]);

  const clearAllAuth = () => {
    // Clear all auth-related data
    Object.keys(localStorage).forEach(key => {
      if (key.includes('supabase') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear session storage
    sessionStorage.clear();
    
    // Reload page
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Auth Diagnostic Page</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="space-x-4">
            <Button onClick={runDiagnostics}>Refresh Diagnostics</Button>
            <Button onClick={clearAllAuth} variant="secondary">Clear All Auth Data</Button>
            <Button onClick={() => window.location.href = '/signin'} variant="secondary">
              Go to Sign In
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Diagnostic Results</h2>
          <pre className="bg-gray-100 p-4 rounded overflow-auto text-xs">
            {JSON.stringify(diagnostics, null, 2)}
          </pre>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <p>Open browser console for additional logs</p>
          <p>Production URL: https://steelman.vercel.app</p>
          <p>Check Network tab for Supabase API calls</p>
        </div>
      </div>
    </div>
  );
};

export default AuthDiagnostic;
